import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginData } from '../types';
import { authApi } from '../services/api';

interface AuthContextType {
  user: User | null;
  login: (data: LoginData) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async (retryCount = 0) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await authApi.getProfile();
      setUser(response.data.user);
      setLoading(false);
    } catch (error: any) {
      console.error('Auth check failed:', error);
      
      // Check if this is a network error or request cancellation (common on mobile)
      const isNetworkError = error.code === 'NETWORK_ERROR' || 
                            error.message?.includes('cancelled') ||
                            error.message?.includes('aborted') ||
                            !error.response;
      
      // Retry only once for network errors, with a longer delay
      if (isNetworkError && retryCount < 1) {
        console.log(`Retrying auth check (attempt ${retryCount + 1}/1) in 2000ms...`);
        setTimeout(() => {
          checkAuthStatus(retryCount + 1);
        }, 2000); // Wait 2 seconds before retry
        return; // Don't set loading false, we're retrying
      }
      
      // Only clear token if it's an actual auth error (401/403), not network issues
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      } else if (!isNetworkError) {
        // For other server errors, also clear tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
      // For network errors, keep tokens and let user try again
      
      setLoading(false);
    }
  };

  const login = async (data: LoginData) => {
    try {
      const response = await authApi.login(data);
      const { user, token, refreshToken } = response.data;
      
      localStorage.setItem('token', token);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      setUser(user);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authApi.logout({ refreshToken });
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};